/**
 * Centralized phone number utilities for consistent validation and formatting
 * across the entire application.
 */
import {
    parsePhoneNumber,
    isValidPhoneNumber as libIsValidPhoneNumber,
} from 'libphonenumber-js';

/** Normalize +E.164-style and common 00 international prefix to +digits for libphonenumber-js. */
function toInternationalPlusForm(value: string): string {
    const t = value.trim();
    if (t.startsWith('00')) {
        const digits = t.replace(/\D/g, '');
        if (digits.startsWith('00')) {
            return '+' + digits.slice(2);
        }
    }
    if (t.startsWith('+')) {
        return '+' + t.slice(1).replace(/\D/g, '');
    }
    return t;
}

/**
 * Validates if a phone number is in a valid format.
 * Uses libphonenumber-js for international (+ / 00) numbers so valid country codes (e.g. +49) work.
 * Falls back to E.164-shaped regex when lib rejects (e.g. legacy US test fixtures) and for US 10/11-digit domestic input.
 */
export const isValidPhoneNumber = (value: string): boolean => {
    if (!value || value.trim() === '') {
        return false; // Empty is invalid for required fields
    }
    const t = value.trim();
    try {
        if (t.startsWith('+') || t.startsWith('00')) {
            const normalized = toInternationalPlusForm(t);
            if (libIsValidPhoneNumber(normalized)) return true;
            return /^\+\d{10,15}$/.test(normalized);
        }
        const digits = t.replace(/\D/g, '');
        if (digits.length === 10) {
            return libIsValidPhoneNumber(digits, 'US') || /^\d{10}$/.test(digits);
        }
        if (digits.length === 11 && digits.startsWith('1')) {
            return libIsValidPhoneNumber('+' + digits, 'US') || /^1\d{10}$/.test(digits);
        }
        return false;
    } catch {
        return false;
    }
};

/**
 * Transforms a phone number to E.164 format
 * @param value - The phone number string to transform
 * @returns string - The phone number in E.164 format
 */
export const transformToE164 = (value: string): string => {
    if (!value || value.trim() === '') {
        return ''; // Return empty string for invalid/empty values
    }
    const t = value.trim();
    try {
        if (t.startsWith('+') || t.startsWith('00')) {
            const normalized = toInternationalPlusForm(t);
            try {
                const parsed = parsePhoneNumber(normalized);
                if (parsed.isValid()) {
                    return parsed.format('E.164');
                }
            } catch {
                /* fall through */
            }
            return normalized;
        }
        const digits = t.replace(/\D/g, '');
        if (digits.length === 10) {
            try {
                const parsed = parsePhoneNumber(digits, 'US');
                if (parsed.isValid()) {
                    return parsed.format('E.164');
                }
            } catch {
                /* fall through */
            }
            return '+1' + digits;
        }
        if (digits.length === 11 && digits.startsWith('1')) {
            const withPlus = '+' + digits;
            try {
                const parsed = parsePhoneNumber(withPlus, 'US');
                if (parsed.isValid()) {
                    return parsed.format('E.164');
                }
            } catch {
                /* fall through */
            }
            return withPlus;
        }
        return digits;
    } catch {
        return t.replace(/\D/g, '');
    }
};

/**
 * Formats a phone number for display purposes.
 * Uses libphonenumber-js to format numbers in their correct international format
 * (e.g. +44 7123 456789 for UK, +1 202 555 1234 for US).
 * @param value - The phone number (E.164 or national format)
 * @returns string - The formatted phone number for display
 */
export const formatPhoneForDisplay = (value: string): string => {
    if (!value || value.trim() === '') {
        return '';
    }
    try {
        const cleaned = value.startsWith('+') ? '+' + value.replace(/[^\d]/g, '') : value.replace(/\D/g, '');
        const defaultCountry = !value.startsWith('+') && (cleaned.length === 10 || (cleaned.length === 11 && cleaned.startsWith('1'))) ? 'US' : undefined;
        const phoneNumber = parsePhoneNumber(cleaned, defaultCountry);
        if (phoneNumber && phoneNumber.isValid()) {
            return phoneNumber.formatInternational();
        }
    } catch {
        // Fall through to return as-is
    }
    return value;
};

/**
 * Cleans a phone number input for processing
 * @param value - The raw phone number input
 * @returns string - The cleaned phone number
 */
export const cleanPhoneInput = (value: string): string => {
    if (!value) return '';
    
    // If it starts with +, preserve it and clean the rest
    if (value.startsWith('+')) {
        return '+' + value.replace(/[^\d]/g, '');
    }
    
    // Otherwise, clean all non-digits
    return value.replace(/\D/g, '');
};

/**
 * Phone validation error message
 */
export const PHONE_VALIDATION_MESSAGE = "Please enter a valid phone number";

/**
 * Phone validation error message for backend
 */
export const PHONE_BACKEND_ERROR_MESSAGE = "Invalid phone number. Please provide a valid international phone number including the country code.";
